import React, { useMemo } from "react";
import { ArrowUpRight, Film, Star } from "lucide-react";
import { useLang } from "@/contexts/LanguageContext";
import { portfolioProjects } from "@/i18n/translations";
import { useMouseParallax } from "@/hooks/useMouseParallax";

/**
 * PORTFOLIO HERO — "Gallery Flythrough"
 * Three vertical image marquees moving at different speeds behind asymmetric type.
 * Mouse-follow spotlight + scanline overlay.
 */
const PortfolioHero = ({ onFilter }) => {
  const { t, lang } = useLang();
  const { ref, pos } = useMouseParallax();

  // Split images into 3 balanced columns for vertical marquees
  const columns = useMemo(() => {
    const imgs = portfolioProjects.map((p) => ({ src: p.image, name: p.name, tag: p.tags[0] }));
    const cols = [[], [], []];
    imgs.forEach((img, i) => cols[i % 3].push(img));
    // Duplicate each column so marquee-y loops seamlessly
    return cols.map((c) => [...c, ...c, ...c]);
  }, []);

  const stats = [
    { v: "80+", l: lang === "bg" ? "Проекта" : "Projects" },
    { v: "4.9", l: lang === "bg" ? "Средна оценка" : "Avg. rating" },
    { v: "15", l: lang === "bg" ? "Индустрии" : "Industries" },
    { v: "€2.4M", l: lang === "bg" ? "Генериран ROAS" : "Revenue driven" },
  ];

  return (
    <section
      ref={ref}
      data-testid="portfolio-hero"
      className="relative border-b border-brand-border overflow-hidden min-h-[92vh] flex items-center scanlines"
    >
      {/* Background image columns */}
      <div className="absolute inset-0 grid grid-cols-3 gap-4 p-4 opacity-20 pointer-events-none">
        {columns.map((col, i) => (
          <div key={i} className="relative overflow-hidden">
            <div
              className={
                i === 0 ? "marquee-y-slow" : i === 1 ? "marquee-y-reverse" : "marquee-y"
              }
            >
              {col.map((img, j) => (
                <div key={j} className="aspect-[3/4] mb-4 rounded-xl overflow-hidden border border-brand-border">
                  <img src={img.src} alt={img.name} className="w-full h-full object-cover grayscale contrast-110" />
                </div>
              ))}
            </div>
          </div>
        ))}
      </div>

      {/* Dim overlay so text stays crisp */}
      <div className="absolute inset-0 pointer-events-none" style={{
        background: "radial-gradient(60% 60% at 35% 50%, rgba(10,10,26,0.4) 0%, rgba(10,10,26,0.85) 65%, rgba(10,10,26,0.98) 100%)"
      }} />

      {/* Mouse-follow holographic spotlight */}
      <div
        className="absolute inset-0 pointer-events-none opacity-60"
        style={{
          background: `radial-gradient(420px circle at ${(pos.x + 0.5) * 100}% ${(pos.y + 0.5) * 100}%, rgba(168,85,247,0.28), transparent 60%)`,
        }}
      />

      <div className="absolute inset-0 pointer-events-none">
        <div className="orb orb-pink w-[560px] h-[560px] -top-32 right-0 opacity-20 animate-float-slower" />
      </div>

      <div className="relative mx-auto max-w-[1400px] px-6 lg:px-10 py-20 pt-5 md:py-28 md:pt-7 w-full">
        <div className="inline-flex items-center gap-3 px-4 py-2 rounded-full border border-brand-border bg-brand-bg/70 backdrop-blur-md mb-8">
          <Film className="w-3.5 h-3.5 text-brand-pinkLight" />
          <span className="font-mono text-[10px] uppercase tracking-[0.3em] text-zinc-300">
            {lang === "bg" ? "// галерия.80" : "// gallery.80"}
          </span>
        </div>

        <p className="text-xs font-mono uppercase tracking-[0.35em] text-brand-purpleLight mb-6 ml-1">{t.portfolio.tag}</p>

        <h1 className="font-heading font-black uppercase leading-[0.82] tracking-tighter">
          <span className="block text-[3rem] md:text-[5.5rem] lg:text-[7rem]">
            {lang === "bg" ? "Проекти" : "Work"}
          </span>
          <span className="block text-[2.4rem] md:text-[4.5rem] lg:text-[6rem] text-outline pl-[10%]">
            {lang === "bg" ? "които" : "that"}
          </span>
          <span className="block text-[3rem] md:text-[5.5rem] lg:text-[7rem] chrome-text pl-[20%]">
            {lang === "bg" ? "генерират." : "converts."}
          </span>
        </h1>

        <p className="mt-8 max-w-xl text-zinc-300 leading-relaxed text-base md:text-lg">
          {lang === "bg"
            ? "Всеки кадър — реален бизнес. Всяка метрика — верифицирана. Разгледайте как трансформираме амбиция в измерим растеж."
            : "Every frame — a real business. Every metric — verified. See how we turn ambition into measurable growth."}
        </p>

        {/* Stats bar */}
        <div className="mt-12 flex flex-wrap gap-[1px] rounded-2xl overflow-hidden border border-brand-border bg-brand-border">
          {stats.map((s) => (
            <div key={s.l} className="flex-1 min-w-[130px] bg-brand-bg/85 backdrop-blur-md px-6 py-5 hover:bg-brand-surface/80 transition-colors group">
              <p className="font-heading text-3xl md:text-4xl font-black tracking-tighter text-white group-hover:chrome-text transition-all">
                {s.v}
              </p>
              <p className="mt-1 text-[10px] font-mono uppercase tracking-[0.25em] text-zinc-500">{s.l}</p>
            </div>
          ))}
        </div>

        <div className="mt-10 flex items-center gap-3">
          <button
            onClick={onFilter}
            data-testid="portfolio-hero-scroll"
            className="inline-flex items-center gap-2 btn-holo text-white font-bold uppercase tracking-wide px-7 py-5 rounded-full text-xs"
          >
            <Star className="w-4 h-4" />
            {lang === "bg" ? "Виж проектите" : "See the work"}
            <ArrowUpRight className="w-4 h-4" strokeWidth={2.5} />
          </button>
          <span className="hidden sm:inline-flex items-center gap-2 font-mono text-[10px] uppercase tracking-[0.3em] text-zinc-400">
            <span className="w-1.5 h-1.5 rounded-full bg-brand-tealLight animate-pulse" />
            {lang === "bg" ? "Актуализирано" : "Synced"}
          </span>
        </div>
      </div>
    </section>
  );
};

export default PortfolioHero;
